import React, {
	ComponentPropsWithoutRef,
	PropsWithoutRef,
	useCallback,
	useEffect,
	useState,
} from "react"

import Button from "../Buttons/Button"
import useTogglePopper from "../../hooks/useTogglePopper"
import Portal from "../Portal"

type DropDownButtonProps = {
	buttonProps?: ComponentPropsWithoutRef<typeof Button>
	dropdownProps?: PropsWithoutRef<JSX.IntrinsicElements["div"]>
	usePortal?: boolean
	disabled?: boolean
}
const DropDownButton: React.FC<DropDownButtonProps> = ({
	children,
	buttonProps,
	dropdownProps,
	usePortal,
	disabled,
}) => {
	const [referenceElement, setReferenceElement] = useState<Element | null>(
		null
	)
	const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
	const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null)
	const { styles, attributes, visible, setVisible } = useTogglePopper(
		referenceElement,
		popperElement,
		{
			modifiers: [
				{ name: "arrow", options: { element: arrowElement } },
				{
					name: "preventOverflow",
					options: {
						altAxis: true,
						padding: 20,
					},
				},
				{
					name: "offset",
					options: {
						offset: [0, 10],
					},
				},
			],
		}
	)

	const toggleVisible = useCallback(() => {
		if (disabled) {
			return
		}

		setVisible((visible) => !visible)
	}, [setVisible, disabled])

	useEffect(() => {
		if (!disabled) {
			return
		}

		setVisible(false)
	}, [disabled, setVisible])

	return (
		<>
			<Button
				{...buttonProps}
				onClick={toggleVisible}
				ref={setReferenceElement}
				disabled={disabled}
			>
				{buttonProps?.children}
			</Button>

			{visible && (
				<Portal usePortal={usePortal}>
					<div
						{...dropdownProps}
						{...attributes.popper}
						ref={setPopperElement}
						style={styles.popper}
						onClick={toggleVisible}
						className={`${
							dropdownProps?.className || ""
						} bg-white shadow-lg rounded overflow-hidden`}
					>
						{children}

						<div ref={setArrowElement} style={styles.arrow} />
					</div>
				</Portal>
			)}
		</>
	)
}

export default DropDownButton
